import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { functionsService } from '../services/api';
import { Function } from '../types';
import { 
  Plus, 
  LogOut, 
  Code, 
  Clock, 
  Copy,
  Trash2,
  Edit,
  Key,
  Circle,
  RotateCw,
  RefreshCw,
  Square,
  FileText,
  X
} from 'lucide-react';
import VariablesTab from './VariablesTab';
import toast from 'react-hot-toast';
import { formatDistanceToNow } from 'date-fns';
import { de } from 'date-fns/locale';

const Dashboard: React.FC = () => {
  const [functions, setFunctions] = useState<Function[]>([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState<'functions' | 'variables'>('functions');
  const { logout, user } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    loadFunctions();
  }, []);

  const loadFunctions = async () => {
    try {
      const data = await functionsService.getAll();
      setFunctions(data);
    } catch (_error) {
      toast.error('Fehler beim Laden der Functions');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id: string, name: string) => {
    if (!confirm(`Möchten Sie die Function "${name}" wirklich löschen?`)) {
      return;
    }

    try {
      await functionsService.delete(id);
      toast.success('Function erfolgreich gelöscht');
      loadFunctions();
    } catch (_error) {
      toast.error('Fehler beim Löschen der Function');
    }
  };

  const handleCopyUrl = async (id: string) => {
    try {
      const { url } = await functionsService.getUrl(id);
      await navigator.clipboard.writeText(url);
      toast.success('URL in die Zwischenablage kopiert');
    } catch (_error) {
      toast.error('Fehler beim Kopieren der URL');
    }
  };

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  if (loading) {
    return (
      <div style={{ height: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <div style={{ 
          width: '32px', 
          height: '32px', 
          border: '3px solid #f3f4f6', 
          borderTop: '3px solid #2563eb', 
          borderRadius: '50%', 
          animation: 'spin 1s linear infinite' 
        }}></div>
      </div>
    );
  }

  return (
    <div style={{ height: '100vh', display: 'flex', flexDirection: 'column' }}>
      {/* Top Action Bar - Always Visible */}
      <div style={{ borderBottom: '1px solid #e5e7eb', backgroundColor: 'white', padding: '12px 24px' }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <div style={{ display: 'flex', alignItems: 'center' }}>
            <Code className="h-8 w-8" style={{ color: '#2563eb', marginRight: '12px' }} />
            <h1 style={{ fontSize: '24px', fontWeight: '600', color: '#111827', margin: 0 }}>
              Edge Functions
            </h1>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
            <span style={{ fontSize: '14px', color: '#6b7280' }}>
              Angemeldet als {user?.username}
            </span>
            <button
              onClick={handleLogout}
              style={{ 
                display: 'flex', 
                alignItems: 'center', 
                color: '#6b7280', 
                fontSize: '14px',
                padding: '8px 12px',
                borderRadius: '6px',
                border: '1px solid #e5e7eb',
                backgroundColor: 'white',
                cursor: 'pointer'
              }}
            >
              <LogOut className="h-4 w-4 mr-1" />
              Abmelden
            </button>
          </div>
        </div>
      </div>

      {/* Main Content Area - Side by Side Layout */}
      <div style={{ flex: 1, display: 'flex', height: 'calc(100vh - 80px)' }}>
        {/* Left Sidebar - Navigation (Fixed Width) */}
        <div style={{ width: '320px', backgroundColor: '#f9fafb', borderRight: '1px solid #e5e7eb', display: 'flex', flexDirection: 'column' }}>
          {/* Navigation Header */}
          <div style={{ padding: '16px', borderBottom: '1px solid #e5e7eb' }}>
            <h3 style={{ fontSize: '14px', fontWeight: '600', color: '#111827', margin: '0 0 12px 0' }}>MANAGE</h3>
            
            {/* Navigation Items */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
              <button
                onClick={() => setActiveTab('functions')}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  padding: '12px',
                  borderRadius: '6px',
                  backgroundColor: activeTab === 'functions' ? '#f3f4f6' : 'transparent',
                  border: 'none',
                  cursor: 'pointer',
                  width: '100%',
                  textAlign: 'left'
                }}
              >
                <Code className="h-4 w-4 mr-3" style={{ color: '#6b7280' }} />
                <span style={{ fontSize: '14px', color: '#111827', fontWeight: '500' }}>Functions</span>
              </button>
              
              <button
                onClick={() => setActiveTab('variables')}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  padding: '12px',
                  borderRadius: '6px',
                  backgroundColor: activeTab === 'variables' ? '#f3f4f6' : 'transparent',
                  border: 'none',
                  cursor: 'pointer',
                  width: '100%',
                  textAlign: 'left'
                }}
              >
                <Key className="h-4 w-4 mr-3" style={{ color: '#6b7280' }} />
                <span style={{ fontSize: '14px', color: '#111827', fontWeight: '500' }}>Variables</span>
              </button>
            </div>
          </div>
        </div>

        {/* Right Side - Content Area (Takes remaining space) */}
        <div style={{ flex: 1, backgroundColor: 'white', overflow: 'auto' }}>
          {activeTab === 'functions' ? (
            <FunctionsContent 
              functions={functions} 
              onDelete={handleDelete} 
              onCopyUrl={handleCopyUrl}
            />
          ) : (
            <VariablesTab />
          )}
        </div>
      </div>
    </div>
  );
};

// Functions Content Component
const FunctionsContent: React.FC<{
  functions: Function[];
  onDelete: (id: string, name: string) => void;
  onCopyUrl: (id: string) => void;
}> = ({ functions, onDelete, onCopyUrl }) => {
  const [containerStatus, setContainerStatus] = React.useState<Record<string, 'healthy' | 'unhealthy' | 'not_running' | 'loading'>>({});
  const [restartingId, setRestartingId] = React.useState<string | null>(null);
  const [stoppingId, setStoppingId] = React.useState<string | null>(null);
  const [refreshingId, setRefreshingId] = React.useState<string | null>(null);
  const [logsOverlay, setLogsOverlay] = React.useState<{ functionName: string; logs: string } | null>(null);
  const [loadingLogsId, setLoadingLogsId] = React.useState<string | null>(null);

  const handleShowLogs = React.useCallback(async (func: Function) => {
    setLoadingLogsId(func.id);
    try {
      const { logs } = await functionsService.getLogs(func.id);
      setLogsOverlay({ functionName: func.name, logs });
    } catch (_error) {
      toast.error('Fehler beim Laden der Logs');
    } finally {
      setLoadingLogsId(null);
    }
  }, []);

  const handleRestart = React.useCallback(async (id: string) => {
    setRestartingId(id);
    try {
      await functionsService.restart(id);
      setContainerStatus((prev) => ({ ...prev, [id]: 'loading' }));
      const { status } = await functionsService.getHealth(id);
      setContainerStatus((prev) => ({ ...prev, [id]: status }));
      toast.success('Container neu gestartet');
    } catch (_error) {
      toast.error('Fehler beim Neustart des Containers');
    } finally {
      setRestartingId(null);
    }
  }, []);

  const handleStop = React.useCallback(async (id: string) => {
    setStoppingId(id);
    try {
      await functionsService.stop(id);
      setContainerStatus((prev) => ({ ...prev, [id]: 'not_running' }));
      toast.success('Container gestoppt');
    } catch (_error) {
      toast.error('Fehler beim Stoppen des Containers');
    } finally {
      setStoppingId(null);
    }
  }, []);

  const fetchAllHealth = React.useCallback(async () => {
    const updates: Record<string, 'healthy' | 'unhealthy' | 'not_running'> = {};
    await Promise.all(
      functions.map(async (func) => {
        try {
          const { status } = await functionsService.getHealth(func.id);
          updates[func.id] = status;
        } catch {
          updates[func.id] = 'unhealthy';
        }
      })
    );
    setContainerStatus((prev) => ({ ...prev, ...updates }));
  }, [functions]);

  const fetchHealthForFunction = React.useCallback(async (id: string) => {
    setRefreshingId(id);
    setContainerStatus((prev) => ({ ...prev, [id]: 'loading' }));
    try {
      const { status } = await functionsService.getHealth(id);
      setContainerStatus((prev) => ({ ...prev, [id]: status }));
    } catch {
      setContainerStatus((prev) => ({ ...prev, [id]: 'unhealthy' }));
    } finally {
      setRefreshingId(null);
    }
  }, []);

  React.useEffect(() => {
    if (functions.length === 0) return;
    functions.forEach((f) => setContainerStatus((prev) => ({ ...prev, [f.id]: 'loading' })));
    fetchAllHealth();
  }, [functions, fetchAllHealth]);

  return (
    <>
    <div style={{ padding: '24px' }}>
      {/* Header Section */}
      <div style={{ marginBottom: '32px' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
          <div>
            <h2 style={{ fontSize: '32px', fontWeight: '700', color: '#111827', margin: '0 0 8px 0' }}>
              Edge Functions
            </h2>
            <p style={{ fontSize: '16px', color: '#6b7280', margin: 0 }}>
              Stellen Sie Edge Functions bereit, um komplexe Geschäftslogik zu handhaben.
            </p>
          </div>
          <Link
            to="/create"
            style={{
              display: 'flex',
              alignItems: 'center',
              padding: '12px 20px',
              backgroundColor: '#2563eb',
              color: 'white',
              fontSize: '14px',
              fontWeight: '500',
              borderRadius: '8px',
              textDecoration: 'none',
              boxShadow: '0 1px 2px 0 rgba(0, 0, 0, 0.05)'
            }}
          >
            <Plus className="h-4 w-4 mr-2" />
            Neue Function bereitstellen
          </Link>
        </div>
      </div>

      {/* Functions List */}
      <div style={{ backgroundColor: 'white', borderRadius: '12px', border: '1px solid #e5e7eb', overflow: 'hidden' }}>
        {/* Functions Header */}
        <div style={{ padding: '20px 24px', borderBottom: '1px solid #e5e7eb', backgroundColor: '#f9fafb' }}>
          <h3 style={{ fontSize: '18px', fontWeight: '600', color: '#111827', margin: 0 }}>Functions</h3>
        </div>

        {/* Functions Content */}
        <div style={{ padding: '0' }}>
          {functions.length === 0 ? (
            <div style={{ padding: '48px 24px', textAlign: 'center' }}>
              <Code className="h-16 w-16 mx-auto mb-4" style={{ color: '#d1d5db' }} />
              <h3 style={{ fontSize: '18px', fontWeight: '600', color: '#111827', margin: '0 0 8px 0' }}>
                Keine Functions gefunden
              </h3>
              <p style={{ fontSize: '14px', color: '#6b7280', margin: '0 0 24px 0' }}>
                Erstellen Sie Ihre erste Function, um zu beginnen.
              </p>
              <Link
                to="/create"
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  padding: '12px 20px',
                  backgroundColor: '#2563eb',
                  color: 'white',
                  fontSize: '14px',
                  fontWeight: '500',
                  borderRadius: '8px',
                  textDecoration: 'none'
                }}
              >
                <Plus className="h-4 w-4 mr-2" />
                Neue Function erstellen
              </Link>
            </div>
          ) : (
            functions.map((func, index) => (
              <div
                key={func.id}
                style={{
                  padding: '20px 24px',
                  borderBottom: index < functions.length - 1 ? '1px solid #e5e7eb' : 'none',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  backgroundColor: 'white'
                }}
              >
                {/* Function Info */}
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ display: 'flex', alignItems: 'center', marginBottom: '8px' }}>
                    <div style={{ 
                      width: '32px', 
                      height: '32px', 
                      backgroundColor: '#eff6ff', 
                      borderRadius: '8px', 
                      display: 'flex', 
                      alignItems: 'center', 
                      justifyContent: 'center',
                      marginRight: '12px'
                    }}>
                      <Code className="h-4 w-4" style={{ color: '#2563eb' }} />
                    </div>
                    <Link
                      to={`/edit/${func.id}`}
                      style={{
                        fontSize: '16px',
                        fontWeight: '600',
                        color: '#111827',
                        margin: 0,
                        textDecoration: 'none',
                        cursor: 'pointer'
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.color = '#2563eb';
                        e.currentTarget.style.textDecoration = 'underline';
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.color = '#111827';
                        e.currentTarget.style.textDecoration = 'none';
                      }}
                    >
                      {func.name}
                    </Link>
                  </div>
                  
                  {/* URL */}
                  <div style={{ display: 'flex', alignItems: 'center', marginBottom: '8px' }}>
                    <span style={{ fontSize: '14px', color: '#6b7280', fontFamily: 'monospace', marginRight: '8px' }}>
                      {`https://functions.digitalaisolutions.de/api/execute/${func.id}`}
                    </span>
                    <button
                      onClick={() => onCopyUrl(func.id)}
                      style={{ 
                        padding: '4px', 
                        color: '#9ca3af',
                        backgroundColor: 'transparent',
                        border: 'none',
                        cursor: 'pointer',
                        borderRadius: '4px'
                      }}
                      title="URL kopieren"
                    >
                      <Copy className="h-4 w-4" />
                    </button>
                  </div>

                  {/* Metadata */}
                  <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
                    <div style={{ display: 'flex', alignItems: 'center', fontSize: '12px', color: '#6b7280' }}>
                      <Clock className="h-4 w-4 mr-1" />
                      {formatDistanceToNow(new Date(func.updated_at), {
                        addSuffix: true,
                        locale: de,
                      })}
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                      <div style={{ 
                        display: 'inline-flex', 
                        alignItems: 'center', 
                        padding: '4px 8px', 
                        backgroundColor: '#dcfce7', 
                        color: '#166534',
                        fontSize: '12px',
                        fontWeight: '500',
                        borderRadius: '6px'
                      }}>
                        {func.deployments} Bereitstellung{func.deployments !== 1 ? 'en' : ''}
                      </div>
                      <button
                        onClick={(e) => { e.stopPropagation(); handleShowLogs(func); }}
                        disabled={loadingLogsId === func.id}
                        title="Container-Logs anzeigen"
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          padding: '4px 8px',
                          backgroundColor: '#eff6ff',
                          color: '#2563eb',
                          border: '1px solid #dbeafe',
                          borderRadius: '6px',
                          cursor: loadingLogsId === func.id ? 'not-allowed' : 'pointer',
                          fontSize: '12px',
                          opacity: loadingLogsId === func.id ? 0.6 : 1,
                        }}
                      >
                        <FileText className="h-3 w-3 mr-1" />
                        {loadingLogsId === func.id ? '...' : 'Logs'}
                      </button>
                    </div>
                  </div>
                </div>

                {/* Actions */}
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                    <span
                      title={
                        containerStatus[func.id] === 'healthy'
                          ? 'Container läuft'
                          : containerStatus[func.id] === 'unhealthy'
                          ? 'Container antwortet nicht'
                          : containerStatus[func.id] === 'not_running'
                          ? 'Container gestoppt'
                          : 'Prüfe Status...'
                      }
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '6px',
                        padding: '8px 12px',
                        borderRadius: '6px',
                        fontSize: '12px',
                        fontWeight: '500',
                        backgroundColor:
                          containerStatus[func.id] === 'healthy'
                            ? '#dcfce7'
                            : containerStatus[func.id] === 'unhealthy'
                            ? '#fef3c7'
                            : containerStatus[func.id] === 'not_running'
                            ? '#fee2e2'
                            : '#f3f4f6',
                        color:
                          containerStatus[func.id] === 'healthy'
                            ? '#166534'
                            : containerStatus[func.id] === 'unhealthy'
                            ? '#b45309'
                            : containerStatus[func.id] === 'not_running'
                            ? '#dc2626'
                            : '#6b7280',
                        border: `1px solid ${
                          containerStatus[func.id] === 'healthy'
                            ? '#bbf7d0'
                            : containerStatus[func.id] === 'unhealthy'
                            ? '#fde68a'
                            : containerStatus[func.id] === 'not_running'
                            ? '#fecaca'
                            : '#e5e7eb'
                        }`,
                        cursor: 'default',
                      }}
                    >
                      <Circle
                        className="h-3 w-3"
                        style={{
                          fill: containerStatus[func.id] === 'healthy' ? '#22c55e' : containerStatus[func.id] === 'not_running' ? '#dc2626' : containerStatus[func.id] === 'unhealthy' ? '#f59e0b' : '#9ca3af',
                          minWidth: '12px',
                        }}
                      />
                      {containerStatus[func.id] === 'healthy'
                        ? 'Läuft'
                        : containerStatus[func.id] === 'unhealthy'
                        ? 'Fehler'
                        : containerStatus[func.id] === 'not_running'
                        ? 'Gestoppt'
                        : '...'}
                    </span>
                    <button
                      onClick={() => fetchHealthForFunction(func.id)}
                      disabled={refreshingId === func.id}
                      title="Status aktualisieren"
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        padding: '6px',
                        backgroundColor: '#f3f4f6',
                        color: '#6b7280',
                        borderRadius: '6px',
                        border: '1px solid #e5e7eb',
                        cursor: refreshingId === func.id ? 'not-allowed' : 'pointer',
                        opacity: refreshingId === func.id ? 0.6 : 1,
                      }}
                    >
                      <RefreshCw className="h-4 w-4" style={{ animation: refreshingId === func.id ? 'spin 1s linear infinite' : undefined }} />
                    </button>
                  </div>
                  <button
                    onClick={() => handleRestart(func.id)}
                    disabled={restartingId === func.id}
                    title="Neustart"
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      padding: '8px',
                      backgroundColor: '#eff6ff',
                      color: '#2563eb',
                      borderRadius: '6px',
                      border: '1px solid #dbeafe',
                      cursor: restartingId === func.id ? 'not-allowed' : 'pointer',
                      opacity: restartingId === func.id ? 0.6 : 1,
                    }}
                  >
                    <RotateCw className={`h-4 w-4 ${restartingId === func.id ? 'animate-spin' : ''}`} />
                  </button>
                  {(containerStatus[func.id] === 'healthy' || containerStatus[func.id] === 'unhealthy') && (
                    <button
                      onClick={() => handleStop(func.id)}
                      disabled={stoppingId === func.id}
                      title="Container stoppen"
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        padding: '8px',
                        backgroundColor: '#fef3c7',
                        color: '#b45309',
                        borderRadius: '6px',
                        border: '1px solid #fde68a',
                        cursor: stoppingId === func.id ? 'not-allowed' : 'pointer',
                        opacity: stoppingId === func.id ? 0.6 : 1,
                      }}
                    >
                      <Square className="h-4 w-4" style={{ fill: 'currentColor' }} />
                    </button>
                  )}
                  <Link
                    to={`/edit/${func.id}`}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      padding: '8px',
                      backgroundColor: '#eff6ff',
                      color: '#2563eb',
                      borderRadius: '6px',
                      textDecoration: 'none',
                      border: '1px solid #dbeafe'
                    }}
                    title="Function bearbeiten"
                  >
                    <Edit className="h-4 w-4" />
                  </Link>
                  <button
                    onClick={() => onDelete(func.id, func.name)}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      padding: '8px',
                      backgroundColor: '#fef2f2',
                      color: '#dc2626',
                      borderRadius: '6px',
                      border: '1px solid #fecaca',
                      cursor: 'pointer'
                    }}
                    title="Function löschen"
                  >
                    <Trash2 className="h-4 w-4" />
                  </button>
                </div>
              </div>
            ))
          )}
        </div>
      </div>
    </div>

    {/* Logs Overlay */}
    {logsOverlay && (
      <div
        style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0,0,0,0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 9999,
          padding: '24px',
        }}
        onClick={() => setLogsOverlay(null)}
      >
        <div
          style={{
            backgroundColor: 'white',
            borderRadius: '12px',
            maxWidth: '900px',
            width: '100%',
            maxHeight: '80vh',
            display: 'flex',
            flexDirection: 'column',
            boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25)',
          }}
          onClick={(e) => e.stopPropagation()}
        >
          <div style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            padding: '16px 20px',
            borderBottom: '1px solid #e5e7eb',
          }}>
            <h3 style={{ margin: 0, fontSize: '18px', fontWeight: '600', color: '#111827' }}>
              Container-Logs: {logsOverlay.functionName}
            </h3>
            <button
              onClick={() => setLogsOverlay(null)}
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                width: '36px',
                height: '36px',
                borderRadius: '8px',
                border: '1px solid #e5e7eb',
                backgroundColor: 'white',
                cursor: 'pointer',
                color: '#6b7280',
              }}
              title="Schließen"
            >
              <X className="h-5 w-5" />
            </button>
          </div>
          <pre style={{
            flex: 1,
            overflow: 'auto',
            padding: '20px',
            margin: 0,
            fontSize: '12px',
            fontFamily: 'monospace',
            backgroundColor: '#1f2937',
            color: '#e5e7eb',
            borderRadius: '0 0 12px 12px',
            whiteSpace: 'pre-wrap',
            wordBreak: 'break-word',
          }}>
            {logsOverlay.logs || '(Keine Logs)'}
          </pre>
        </div>
      </div>
    )}
    </>
  );
};


export default Dashboard;
